import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSector } from "@/lib/sectorContext";
import { Plus, PencilSimple, Trash, FloppyDisk, X } from "@phosphor-icons/react";

// v0.98 · Fev/2026 — Triggers sector-aware.
// "vehicle_ready" e "before_pickup" só fazem sentido em setores que
// entregam item físico ao cliente (automotive, tech_repair). Em outros
// eles somem ou viram genéricos ("Antes do agendamento").
const TRIGGERS_AUTOMOTIVE = [
  { value: "inbound_inquiry",        label: "Cliente perguntou" },
  { value: "post_booking",           label: "Após agendamento" },
  { value: "appointment_completed",  label: "Após o atendimento (cross-sell)" },
  { value: "vehicle_ready",          label: "Quando o veículo fica pronto" },
  { value: "before_pickup",          label: "Antes da retirada do veículo" },
  { value: "inactive_customer",      label: "Cliente inativo" },
  { value: "vip_customer",           label: "Cliente VIP" },
  { value: "manual",                 label: "Manual" },
];
const TRIGGERS_GENERIC = [
  { value: "inbound_inquiry",        label: "Cliente perguntou" },
  { value: "post_booking",           label: "Após agendamento" },
  { value: "appointment_completed",  label: "Após o atendimento (cross-sell)" },
  { value: "before_appointment",     label: "Antes do agendamento" },
  { value: "inactive_customer",      label: "Cliente inativo" },
  { value: "vip_customer",           label: "Cliente VIP" },
  { value: "manual",                 label: "Manual" },
];

const AUDIENCES = [
  { value: "all", label: "Todos" },
  { value: "vip", label: "VIPs" },
  { value: "inactive", label: "Inativos" },
  { value: "new_lead", label: "Novo lead" },
];

const empty = {
  title: "",
  description: "",
  price_cents: 0,
  original_price_cents: null,
  valid_from: "",
  valid_until: "",
  active: true,
  trigger: "inbound_inquiry",
  audience: "all",
};

function brl(cents) {
  if (cents == null) return "—";
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 2,
  });
}

function isActiveNow(p) {
  if (!p.active) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (p.valid_from && p.valid_from > today) return false;
  if (p.valid_until && p.valid_until < today) return false;
  return true;
}

export default function PromotionsTab({ onChange, onToast }) {
  const { isAutomotive } = useSector();
  const TRIGGERS = isAutomotive ? TRIGGERS_AUTOMOTIVE : TRIGGERS_GENERIC;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/operacao/promotions");
      setItems(res.data?.items || []);
    } catch (e) {
      onToast?.("Erro ao carregar ofertas.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title?.trim()) {
      onToast?.("Título obrigatório.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        description: editing.description || "",
        price_cents: Number(editing.price_cents) || 0,
        original_price_cents: editing.original_price_cents
          ? Number(editing.original_price_cents) : null,
        valid_from: editing.valid_from || null,
        valid_until: editing.valid_until || null,
        active: editing.active !== false,
        trigger: editing.trigger || "inbound_inquiry",
        audience: editing.audience || "all",
      };
      if (editing.id) {
        await api.patch(`/operacao/promotions/${editing.id}`, payload);
        onToast?.("Oferta atualizada.");
      } else {
        await api.post("/operacao/promotions", payload);
        onToast?.("Oferta criada.");
      }
      setEditing(null);
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta oferta?")) return;
    try {
      await api.delete(`/operacao/promotions/${id}`);
      onToast?.("Oferta removida.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao remover.", "error");
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  const activeCount = items.filter(isActiveNow).length;

  return (
    <div className="space-y-6" data-testid="promotions-tab">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate-500">
          {activeCount} ativa{activeCount === 1 ? "" : "s"} agora · {items.length} no total.
        </p>
        <button
          type="button"
          data-testid="promotions-add"
          onClick={() => setEditing({ ...empty })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px]"
        >
          <Plus size={12} weight="bold" /> Nova oferta
        </button>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-10 rounded-2xl border border-dashed border-slate-200 text-center">
          <p className="text-sm text-slate-500">Nenhuma oferta cadastrada.</p>
          <p className="text-[12px] text-slate-400 mt-1">
            A IA NÃO pode mencionar "oferta" ou "desconto" enquanto vazio.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((p) => {
            const live = isActiveNow(p);
            return (
              <div
                key={p.id}
                data-testid={`promotion-card-${p.id}`}
                className="rounded-2xl border border-slate-100 p-4 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-slate-900 truncate">{p.title}</h4>
                      <span className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 ${
                        live
                          ? "bg-slate-900 text-white"
                          : "border border-slate-200 text-slate-400"
                      }`}>
                        {live ? "ativa" : "pausada"}
                      </span>
                    </div>
                    {p.description && (
                      <p className="text-[12px] text-slate-500 mt-1 line-clamp-2">{p.description}</p>
                    )}
                    <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-2 flex-wrap">
                      <span>{TRIGGERS.find((t) => t.value === p.trigger)?.label}</span>
                      <span>·</span>
                      <span>{AUDIENCES.find((a) => a.value === p.audience)?.label}</span>
                      {(p.valid_from || p.valid_until) && (
                        <>
                          <span>·</span>
                          <span>{p.valid_from || "—"} a {p.valid_until || "—"}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">
                      {brl(p.price_cents)}
                    </div>
                    {p.original_price_cents && (
                      <div className="text-[11px] text-slate-400 line-through tabular-nums">
                        {brl(p.original_price_cents)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(p)}
                    data-testid={`promotion-edit-${p.id}`}
                    className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    data-testid={`promotion-remove-${p.id}`}
                    className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" data-testid="promotion-modal">
          <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {editing.id ? "Editar oferta" : "Nova oferta"}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-500"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <input
                type="text"
                placeholder="Título da oferta"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
                data-testid="promotion-modal-title"
              />
              <textarea
                placeholder="Descrição (opcional)"
                value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 rounded-2xl border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none resize-none"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Preço</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editing.price_cents ? (editing.price_cents / 100) : ""}
                    onChange={(e) => setEditing({
                      ...editing,
                      price_cents: Math.round((parseFloat(e.target.value) || 0) * 100),
                    })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none tabular-nums"
                    data-testid="promotion-modal-price"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Preço original (opcional)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editing.original_price_cents ? (editing.original_price_cents / 100) : ""}
                    onChange={(e) => setEditing({
                      ...editing,
                      original_price_cents: e.target.value
                        ? Math.round((parseFloat(e.target.value) || 0) * 100)
                        : null,
                    })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none tabular-nums"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Válida de</label>
                  <input
                    type="date"
                    value={editing.valid_from || ""}
                    onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">até</label>
                  <input
                    type="date"
                    value={editing.valid_until || ""}
                    onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Gatilho</label>
                  <select
                    value={editing.trigger}
                    onChange={(e) => setEditing({ ...editing, trigger: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
                  >
                    {TRIGGERS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Audiência</label>
                  <select
                    value={editing.audience}
                    onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
                  >
                    {AUDIENCES.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                <input
                  type="checkbox"
                  checked={editing.active !== false}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                Ativa
              </label>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100 text-[13px]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="promotion-modal-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] disabled:opacity-50"
              >
                <FloppyDisk size={12} weight="bold" />
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
